/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { Resource } from 'i18next';
import { ThemeProvider } from 'styled-components';
import { theme } from '@marriott/mi-ui-library-shop';
import { ComponentProps } from './index.types';
import { useConditionalPageContextSERP } from '../../hooks';
import { getSearchQueryDataFromSession } from './lib/helper';
import { getSessionIDFromCookie } from '../../utils/src/helper';
import { ErrorHandlerAlert } from '../../molecules';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import SearchResults from './searchResultsMainComponent';

interface SearchResultsProps {
  miSearchResults: Resource;
  searchResults: ComponentProps;
  model?: Record<string, any>;
  isAuthorMode: boolean;
  AiSearchResults?: any;
  onOpenHotelPanel?: (propertyData: any, propertyId: string) => void;
  onPropertyMediaLoaded?: (propertiesMedia: any[]) => void;
  isStaticMap?: boolean; // Controls whether the map is static (non-interactive) or interactive
  isMapLoading?: boolean; // Controls whether the map loader is shown (used with isStaticMap)
  messageUiData?: any;
  aiFilterUpdateData?: any; // NLS facets result to update filter modal counts (chat context only)
  aiPreSelectedFacets?: any; // NLP-entity derived pre-selections: shows filters as checked + badge count (chat context only)
}

export const SearchResultsComponent: React.FC<SearchResultsProps> = pageProps => {
  const { currentLocale, sessionData: pageSessionData, isAuthorMode } = useConditionalPageContextSERP();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [initialState] = useState<any>();
  const searchResultProps = pageProps?.model || pageProps;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [jsonContent, setJsonContent] = useState<any>(searchResultProps);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [updatedTheme, setUpdatedTheme] = useState<any>({ ...theme, dir: '' });

  const sessionData = pageSessionData?.data ? pageSessionData?.data : pageSessionData?.cacheData?.data;

  let sessionToken = '';
  if (sessionData) {
    const sessionObj = sessionData?.data ? sessionData : sessionData?.cacheData;
    sessionToken = sessionObj?.data?.sessionToken || sessionObj?.key || getSessionIDFromCookie() || '';
  }
  const newSearchQueryData = getSearchQueryDataFromSession(sessionData, sessionToken, currentLocale);

  useEffect(() => {
    const searchResultProps = pageProps?.model || pageProps;
    // pageProps = { ...pageProps };

    // setInitialState({});
    setJsonContent(searchResultProps);
    setUpdatedTheme({
      ...theme,
      dir: '',
    });
  }, [pageProps]);

  const ampRequest =
    pageProps?.AiSearchResults?.rawData?.data?.search?.conversationalSearch?.searchByNaturalLanguageQuery?.ampRequest;

  const renderComponent = () => {
    return (
      <ErrorHandlerAlert alertMessage={jsonContent?.['componentErrorMessage']}>
        <ThemeProvider theme={updatedTheme}>
          <SearchResults
            {...initialState}
            jsonContent={jsonContent}
            searchQueryData={newSearchQueryData}
            completeSession={pageSessionData?.data ? pageSessionData : pageSessionData?.cacheData}
            isAuthorMode={isAuthorMode}
            AiSearchResults={pageProps.AiSearchResults}
            onOpenHotelPanel={pageProps.onOpenHotelPanel}
            onPropertyMediaLoaded={pageProps.onPropertyMediaLoaded}
            isStaticMap={pageProps.isStaticMap}
            isMapLoading={pageProps.isMapLoading}
            messageUiData={pageProps.messageUiData}
            aiFilterUpdateData={pageProps.aiFilterUpdateData}
            aiPreSelectedFacets={pageProps.aiPreSelectedFacets}
            ampRequest={ampRequest}
          />
        </ThemeProvider>
      </ErrorHandlerAlert>
    );
  };

  return renderComponent();
};

//Config for aem editable components
export const SearchResultsComponentConfig = {
  emptyLabel: 'SearchResults',
  isEmpty: false,
  resourceType: `mi-aem-shop-spa/components/content/searchresults/v1/searchresults`,
};
//Component wrapped with editable component for aem authoring
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SearchResultsComponentEditable = (props: any) =>
  props.cqPath.includes('experiencefragment') ? (
    <SearchResultsComponent {...props} />
  ) : (
    <EditableComponent config={SearchResultsComponentConfig} {...props}>
      <SearchResultsComponent {...props} />
    </EditableComponent>
  );
